import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  PhoneIphone as CellPhoneIcon,
  Home as HomePhoneIcon,
  Email as EmailIcon,
  AlternateEmail as AltEmailIcon,
} from '@mui/icons-material';

interface ContactData {
  // v3 flat fields
  WorkPhone?: string;
  CellPhone?: string;
  HomePhone?: string;
  WorkEmail?: string;
  EmailWork?: string;
  PersonalEmail?: string;
  OtherEmail?: string;
  // v3.2 nested fields
  phoneWork?: { number?: string };
  phoneMobile?: { number?: string };
  phoneOther?: { number?: string };
  emailWork?: string;
  emailPersonal?: string;
  emailOther?: string;
  [key: string]: unknown;
}

interface FieldDef {
  key: string;
  label: string;
  type: 'phone' | 'email';
  icon: React.ReactNode;
  optional: boolean;
  getValue: (contact: ContactData) => string;
}

const FIELDS: FieldDef[] = [
  {
    key: 'workPhone',
    label: 'Work Phone',
    type: 'phone',
    icon: <PhoneIcon />,
    optional: false,
    getValue: (c) =>
      c.WorkPhone || c.phoneWork?.number || '',
  },
  {
    key: 'cellPhone',
    label: 'Cell Phone',
    type: 'phone',
    icon: <CellPhoneIcon />,
    optional: true,
    getValue: (c) =>
      c.CellPhone || c.phoneMobile?.number || '',
  },
  {
    key: 'homePhone',
    label: 'Home Phone',
    type: 'phone',
    icon: <HomePhoneIcon />,
    optional: true,
    getValue: (c) =>
      c.HomePhone || c.phoneOther?.number || '',
  },
  {
    key: 'workEmail',
    label: 'Work Email',
    type: 'email',
    icon: <EmailIcon />,
    optional: false,
    getValue: (c) =>
      c.WorkEmail || c.EmailWork || c.emailWork || '',
  },
  {
    key: 'personalEmail',
    label: 'Personal Email',
    type: 'email',
    icon: <AltEmailIcon />,
    optional: true,
    getValue: (c) =>
      c.PersonalEmail || c.emailPersonal || '',
  },
  {
    key: 'otherEmail',
    label: 'Other Email',
    type: 'email',
    icon: <AltEmailIcon />,
    optional: true,
    getValue: (c) =>
      c.OtherEmail || c.emailOther || '',
  },
];

interface ContactInfoSectionProps {
  contact: ContactData;
}

export default function ContactInfoSection({ contact }: ContactInfoSectionProps) {
  const visibleFields = FIELDS.filter((field) => {
    if (!field.optional) return true;
    return !!field.getValue(contact);
  });

  return (
    <Box>
      <Typography
        variant="overline"
        sx={{
          px: 2,
          pt: 2,
          pb: 0.5,
          display: 'block',
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: 'text.secondary',
        }}
      >
        Contact Information
      </Typography>
      <List disablePadding>
        {visibleFields.map((field) => {
          const value = field.getValue(contact);
          const isEmpty = !value;
          const emptyLabel =
            field.type === 'phone' ? 'No phone' : 'No email';
          const href =
            field.type === 'phone' ? `tel:${value}` : `mailto:${value}`;

          return (
            <ListItem
              key={field.key}
              sx={{
                px: 2,
                py: 1,
                '&:hover': { bgcolor: 'action.hover' },
                transition: 'background-color 0.15s',
              }}
              secondaryAction={
                !isEmpty ? (
                  <Tooltip
                    title={
                      field.type === 'phone'
                        ? `Call ${value}`
                        : `Email ${value}`
                    }
                  >
                    <IconButton
                      edge="end"
                      component="a"
                      href={href}
                      aria-label={
                        field.type === 'phone'
                          ? `Call ${field.label}`
                          : `Send email to ${field.label}`
                      }
                      sx={{ color: 'primary.main' }}
                    >
                      {field.icon}
                    </IconButton>
                  </Tooltip>
                ) : undefined
              }
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                {field.icon}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="caption" color="text.secondary">
                    {field.label}
                  </Typography>
                }
                secondary={
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isEmpty ? 400 : 500,
                      color: isEmpty ? 'text.disabled' : 'text.primary',
                      fontStyle: isEmpty ? 'italic' : 'normal',
                    }}
                  >
                    {isEmpty ? emptyLabel : value}
                  </Typography>
                }
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
